/**
 * Account & Plan Page
 * Modern, professional SaaS subscription screen
 * Clean design with conversion-focused layout
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { useTrial } from '../contexts/TrialContext';
import { useError } from '../contexts/ErrorContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PlatformLayout from './PlatformLayout';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import Modal from './Modal';
import SubscriptionUpgradeModal from './SubscriptionUpgradeModal';
import { formatTrialDate, getTrialStatusColor, getTrialStatusText } from '../utils/trialUtils';
import SubscriptionService from '../services/SubscriptionService';
import { SUBSCRIPTION_PLANS, getPlanById, formatPrice } from '../data/subscriptionPlans';
import { useTranslation } from '../hooks/useTranslation';
import { getPlatformType } from '../utils/platformUtils';
import { Capacitor } from '@capacitor/core';
import clsx from 'clsx';
import './AccountPlan.css';

// Helper functions for subscription display
const getPlanDisplayName = (planId) => {
  const plan = getPlanById(planId);
  if (!plan) return 'Basic Plan';
  
  const displayNames = {
    'basic': 'Basic Plan',
    'premium': 'Premium Plan',
    'premiumPlus': 'Premium+ Plan',
    'enterprise': 'Enterprise Plan'
  };
  
  return displayNames[planId] || plan.name + ' Plan';
};

// Helper function to safely format subscription end date
const formatSubscriptionEndDate = (subscriptionEndsAt) => {
  if (!subscriptionEndsAt) return null;
  
  // Handle Firestore Timestamp
  if (typeof subscriptionEndsAt.toDate === 'function') {
    return formatTrialDate(subscriptionEndsAt.toDate());
  }
  
  // Handle Date object
  if (subscriptionEndsAt instanceof Date) {
    return formatTrialDate(subscriptionEndsAt);
  }
  
  // Handle ISO string or other formats
  const formatted = formatTrialDate(subscriptionEndsAt);
  
  // Return null if formatting failed (so we can use fallback text)
  if (formatted === 'N/A' || formatted === 'Invalid Date') {
    return null;
  }
  
  return formatted;
};

const getPlanPrice = (planId) => {
  const plan = getPlanById(planId);
  if (!plan) return '₹999';
  
  return formatPrice(plan.monthlyPrice);
};

const getPlanBenefits = (planId, tSubscription) => {
  const plan = getPlanById(planId);
  if (!plan) {
    return [
      tSubscription('upgrade.benefits.upToUsers', { count: 6 }),
      tSubscription('upgrade.benefits.upToOrders', { count: 80 }),
      tSubscription('upgrade.benefits.daysBackup', { days: 30 }),
      tSubscription('upgrade.benefits.basicSupport')
    ];
  }
  
  const benefits = [];
  
  // Add user limit
  if (plan.limits.maxUsers === -1) {
    benefits.push(tSubscription('upgrade.benefits.unlimitedUsers'));
  } else {
    benefits.push(tSubscription('upgrade.benefits.upToUsers', { count: plan.limits.maxUsers || 0 }));
  }
  
  // Add order limit
  if (plan.limits.maxOrders === -1) {
    benefits.push(tSubscription('upgrade.benefits.unlimitedOrders'));
  } else {
    benefits.push(tSubscription('upgrade.benefits.upToOrders', { count: plan.limits.maxOrders || 0 }));
  }
  
  // Add backup period
  if (plan.limits.backupDays === -1) {
    benefits.push(tSubscription('upgrade.benefits.foreverBackup'));
  } else {
    benefits.push(tSubscription('upgrade.benefits.daysBackup', { days: plan.limits.backupDays || 0 }));
  }
  
  // Add plan-specific benefits
  if (planId === 'premium' || planId === 'premiumPlus' || planId === 'enterprise') {
    benefits.push(tSubscription('upgrade.benefits.prioritySupport'));
  }
  
  if (planId === 'premiumPlus' || planId === 'enterprise') {
    benefits.push(tSubscription('upgrade.benefits.advancedAnalytics'));
  }
  
  if (planId === 'enterprise') {
    benefits.push(tSubscription('upgrade.benefits.dedicatedAccountManager'));
  }
  
  return benefits.slice(0, 4); // Limit to 4 benefits for clean display
};

const getPlanBenefitsWithDetails = (planId, tSubscription) => {
  const plan = getPlanById(planId);
  if (!plan) {
    return [
      {
        value: tSubscription('upgrade.benefits.users', { count: 6 }),
        description: tSubscription('upgrade.benefits.teamMembers'),
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
      },
      {
        value: tSubscription('upgrade.benefits.orders', { count: 80 }),
        description: tSubscription('upgrade.benefits.monthlyLimit'),
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
      },
      {
        value: tSubscription('upgrade.benefits.days', { count: 30 }),
        description: tSubscription('upgrade.benefits.fileBackup'),
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><path d="M16 13H8"/><path d="M16 17H8"/><polyline points="10,9 9,9 8,9"/></svg>
      },
      {
        value: tSubscription('upgrade.benefits.basicSupport'),
        description: tSubscription('upgrade.benefits.emailAssistance'),
        icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      }
    ];
  }
  
  const benefits = [];
  
  // Add user limit
  if (plan.limits.maxUsers === -1) {
    benefits.push({
      value: tSubscription('upgrade.benefits.unlimitedUsers'),
      description: tSubscription('upgrade.benefits.teamMembers'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
    });
  } else {
    benefits.push({
      value: tSubscription('upgrade.benefits.users', { count: plan.limits.maxUsers || 0 }),
      description: tSubscription('upgrade.benefits.teamMembers'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
    });
  }
  
  // Add order limit
  if (plan.limits.maxOrders === -1) {
    benefits.push({
      value: tSubscription('upgrade.benefits.unlimitedOrders'),
      description: tSubscription('upgrade.benefits.monthlyLimit'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
    });
  } else {
    benefits.push({
      value: tSubscription('upgrade.benefits.orders', { count: plan.limits.maxOrders }),
      description: tSubscription('upgrade.benefits.monthlyLimit'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
    });
  }
  
  // Add backup period
  if (plan.limits.backupDays === -1) {
    benefits.push({
      value: tSubscription('upgrade.benefits.foreverBackup'),
      description: tSubscription('upgrade.benefits.fileRetention'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><path d="M16 13H8"/><path d="M16 17H8"/><polyline points="10,9 9,9 8,9"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
    });
  } else {
    benefits.push({
      value: tSubscription('upgrade.benefits.days', { count: plan.limits.backupDays }),
      description: tSubscription('upgrade.benefits.fileBackup'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><path d="M16 13H8"/><path d="M16 17H8"/><polyline points="10,9 9,9 8,9"/></svg>
    });
  }
  
  // Add plan-specific benefits
  if (planId === 'premium' || planId === 'premiumPlus' || planId === 'enterprise') {
    benefits.push({
      value: tSubscription('upgrade.benefits.prioritySupport'),
      description: tSubscription('upgrade.benefits.dedicatedAssistance'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
    });
  }
  
  if (planId === 'premiumPlus' || planId === 'enterprise') {
    benefits.push({
      value: tSubscription('upgrade.benefits.advancedAnalytics'),
      description: tSubscription('upgrade.benefits.detailedInsights'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
    });
  }
  
  if (planId === 'enterprise') {
    benefits.push({
      value: tSubscription('upgrade.benefits.dedicatedAccountManager'),
      description: tSubscription('upgrade.benefits.dedicatedSupport'),
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
    });
  }
  
  return benefits.slice(0, 4); // Limit to 4 benefits for clean display
};

const getNextTierSuggestion = (planId) => {
  const suggestions = {
    'basic': {
      title: 'Upgrade to Premium →',
      description: 'Get priority support & advanced analytics'
    },
    'premium': {
      title: 'Upgrade to Premium+ →',
      description: 'Get advanced analytics & dedicated support'
    },
    'premiumPlus': {
      title: 'Upgrade to Enterprise →',
      description: 'Get unlimited everything & dedicated manager'
    },
    'enterprise': null // No upgrade suggestion for highest tier
  };
  
  return suggestions[planId] || null;
};

// Helper function to get the next tier plan ID
const getNextTierPlanId = (currentPlanId) => {
  const planHierarchy = ['basic', 'premium', 'premiumPlus', 'enterprise'];
  const currentIndex = planHierarchy.indexOf(currentPlanId);
  
  if (currentIndex === -1 || currentIndex === planHierarchy.length - 1) {
    return null; // No upgrade available
  }
  
  return planHierarchy[currentIndex + 1];
};

const AccountPlan = () => {
  const navigate = useNavigate();
  const { organizationData, userRole, organizationId } = useOrganization();
  const { showErrorToast } = useError();
  const { 
    trialStatus, 
    daysRemaining, 
    trialProgress, 
    isTrialExpired, 
    isOnTrial,
    hasActiveSubscription,
    nextBillingDate,
    willNotRenew,
    subscriptionEndsAt,
    loading,
    error,
    fetchTrialStatus
  } = useTrial();

  // Initialize Firebase Functions
  const functions = getFunctions();

  // Platform detection
  const platformType = getPlatformType();
  const isNative = Capacitor.isNativePlatform();

  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('month');
  const [plansLoading, setPlansLoading] = useState(true);
  const [usageStats, setUsageStats] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const { t } = useTranslation('account');
  const { t: tSubscription } = useTranslation('subscription');
  const [usageError, setUsageError] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  
  

  // Load subscription plans
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setPlansLoading(true);
        const allPlans = SubscriptionService.getAllPlans();
        setPlans(allPlans);
        
        // Set current plan if user has active subscription
        if (hasActiveSubscription && trialStatus?.subscriptionPlan) {
          const current = SubscriptionService.getPlan(trialStatus.subscriptionPlan);
          setCurrentPlan(current);
        }
      } catch (error) {
        // Error loading plans - handled silently
      } finally {
        setPlansLoading(false);
      }
    };

    loadPlans();
  }, [hasActiveSubscription, trialStatus?.subscriptionPlan]);

  // Load usage statistics
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!organizationId) return;
      
      try {
        setUsageLoading(true);
        setUsageError(null);
        const stats = await SubscriptionService.getUsageStats(organizationId);
        setUsageStats(stats);
      } catch (error) {
        setUsageError(error.message);
      } finally {
        setUsageLoading(false);
      }
    };

    loadUsageStats();
  }, [organizationId]);


  // Additional security check - only BusinessOwners can access this page
  if (userRole !== 'BusinessOwner') {
    return (
      <PlatformLayout>
        <div className="saas-account-container">
          <div className="saas-account-error">
            <PlatformText variant="h2" className="saas-error-text" translationKey="errors.accessDenied" namespace="account" />
            <PlatformText variant="body" className="saas-error-text" translationKey="errors.businessOwnerOnly" namespace="account" />
            <PlatformButton 
              onClick={() => navigate('/')} 
              variant="primary"
              className="saas-upgrade-button"
            >
              <PlatformText translationKey="goToDashboard" namespace="account" />
            </PlatformButton>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  const handleUpgrade = (plan = null) => {
    if (userRole !== 'BusinessOwner') {
      showErrorToast('Only Business Owners can manage subscription plans', {
        variant: 'warning',
        autoHide: true,
        duration: 4000
      });
      return;
    }

    if (plan) {
      setSelectedUpgradePlan(plan);
      setShowUpgradeModal(true);
    } else {
      // Set default plan if none provided - use first available plan
      const defaultPlan = SubscriptionService.getAllPlans()[0];
      if (defaultPlan) {
        setSelectedUpgradePlan(defaultPlan);
        setShowUpgradeModal(true);
      } else {
        // Fallback: navigate to subscription plans page
        navigate('/account-plan');
      }
    }
  };

  const handlePlanSelect = (plan) => {
    handleUpgrade(plan);
  };



  const handleContactSupport = () => {
    // TODO: Open support contact
  };

  // Handle upgrade modal close
  const handleUpgradeModalClose = () => {
    setShowUpgradeModal(false);
    setSelectedUpgradePlan(null);
  };

  // Handle upgrade completion
  const handleUpgradeComplete = async (upgradeData) => {
    // Close modal first
    handleUpgradeModalClose();
    
    // Show success message
    showErrorToast(tSubscription('upgrade.upgradeSuccessful', { planName: upgradeData?.planName || 'your plan' }), {
      variant: 'info',
      autoHide: true,
      duration: 4000
    });
    
    // Explicitly refresh trial/subscription status to ensure UI reflects new subscription
    // This is critical because:
    // 1. SubscriptionUpgradeModal already calls fetchTrialStatus, but we want to ensure it happens
    // 2. OrganizationContext real-time listener will update, but we want immediate UI update
    // 3. Handles edge cases where refresh might have failed in SubscriptionUpgradeModal
    try {
      await fetchTrialStatus();
    } catch (error) {
      // Don't show error to user - real-time listener will catch up eventually
    }
  };

  const handleManageSubscription = () => {
    setManageOpen(true);
  };

  // Removed invoice download per design update

  const handleBack = () => {
    // If trial is expired, navigate directly to trial-expired instead of using browser history
    // This prevents users from accessing blocked routes via browser back button
    if (isTrialExpired) {
      navigate('/trial-expired', { replace: true });
    } else {
      navigate(-1);
    }
  };

  // Native-specific plan display component
  const NativePlanDisplay = ({ plans, t }) => (
    <div className="native-plans-display">
      <div className="saas-card-header">
        <div className="saas-card-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </div>
        <div className="saas-card-header-content">
          <PlatformText variant="h3" className="saas-card-title">
            Available Plans
          </PlatformText>
          <PlatformText variant="body" className="saas-card-subtitle">
            Scroll through our subscription plans to choose the best option for you.
          </PlatformText>
        </div>
      </div>

      {/* Plans Grid - Native Version (No Pricing, No Buttons) */}
      <div className="saas-plans-container">
        <div className="saas-plans-scroll-wrapper">
          <div className="saas-plans-grid">
            {plans.map(plan => {
              const isPopular = plan.popular;
              
              return (
                <div 
                  key={plan.id}
                  className={clsx('saas-plan-item', 'native-plan-item', isPopular && 'popular')}
                  role="article"
                  aria-label={`${plan.name} subscription plan`}
                >
                  {isPopular && (
                    <div className="saas-plan-badge">{t('subscription.mostPopular')}</div>
                  )}
                  
                  <div className="saas-plan-header">
                    <PlatformText variant="h4" className="saas-plan-name">
                      {plan.name}
                    </PlatformText>
                    <PlatformText variant="body" className="saas-plan-description">
                      {plan.description}
                    </PlatformText>
                  </div>

                  {/* REMOVED: Pricing section completely for native */}
                  
                  <div className="saas-plan-limits">
                    <div className="saas-limit-item">
                      <PlatformText variant="body" className="saas-limit-value">
                        {plan.limits.maxUsers === -1 ? '∞' : plan.limits.maxUsers}
                      </PlatformText>
                      <PlatformText variant="caption" className="saas-limit-label">
                        Users
                      </PlatformText>
                    </div>
                    <div className="saas-limit-item">
                      <PlatformText variant="body" className="saas-limit-value">
                        {plan.limits.maxOrders === -1 ? '∞' : plan.limits.maxOrders}
                      </PlatformText>
                      <PlatformText variant="caption" className="saas-limit-label">
                        Orders
                      </PlatformText>
                    </div>
                    <div className="saas-limit-item">
                      <PlatformText variant="body" className="saas-limit-value">
                        {plan.limits.backupDays === -1 ? '∞' : `${plan.limits.backupDays}d`}
                      </PlatformText>
                      <PlatformText variant="caption" className="saas-limit-label">
                        Backup
                      </PlatformText>
                    </div>
                  </div>

                  {/* REMOVED: Plan selection button completely for native */}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Website information text for native platforms */}
      <div className="native-website-info">
        <PlatformText variant="body" className="native-info-text">
          Subscription changes are not supported in this app. Please use your organization’s web portal for plan management.
        </PlatformText>
      </div>
    </div>
  );

  const closeManageDialog = () => setManageOpen(false);

  const handleCancelSubscription = async () => {
    // Confirmation dialog
    if (!window.confirm('Are you sure you want to cancel your subscription? Your subscription will remain active until the end of the current billing period, after which it will not renew.')) {
      return;
    }

    setPlansLoading(true);
    
    try {
      // Call backend Firebase Function
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      
      const result = await cancelSubscription({ organizationId });
      
      if (result.data.success) {
        showErrorToast(
          result.data.alreadyCancelled 
            ? result.data.message 
            : 'Subscription cancellation confirmed. Your subscription will remain active until the end of the current billing period.',
          {
            variant: 'info',
            autoHide: true,
            duration: 6000
          }
        );
        
        // Refresh subscription status
        await fetchTrialStatus();
      }
    } catch (error) {
      // Handle specific error cases
      if (error.code === 'failed-precondition') {
        showErrorToast(error.message, {
          variant: 'warning',
          autoHide: true,
          duration: 5000
        });
      } else if (error.code === 'permission-denied') {
        showErrorToast('Only Business Owners can cancel subscriptions', {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
      } else {
        showErrorToast('Failed to cancel subscription. Please try again.', {
          variant: 'error',
          autoHide: true,
          duration: 4000
        });
      }
    } finally {
      setPlansLoading(false);
      setManageOpen(false);
    }
  };

  const handleUpgradePlanFromDialog = () => {
    // Get the next tier plan based on current subscription
    const currentPlanId = displayTrialStatus?.subscriptionPlan;
    const nextPlanId = getNextTierPlanId(currentPlanId);
    
    if (nextPlanId) {
      const nextPlan = SubscriptionService.getPlan(nextPlanId);
      if (nextPlan) {
        handleUpgrade(nextPlan);
        setManageOpen(false);
        return;
      }
    }
    
    // If no upgrade available (already on Enterprise), show message
    showErrorToast('You are already on the highest tier plan', {
      variant: 'info',
      autoHide: true,
      duration: 4000
    });
    setManageOpen(false);
  };


  // Use real trial context
  const displayTrialStatus = {
    trialStatus: trialStatus?.trialStatus, // Extract the nested trialStatus string property ('active', 'expired', 'not_started')
    daysRemaining,
    trialProgress,
    isTrialExpired,
    isOnTrial,
    hasActiveSubscription,
    subscriptionPlan: trialStatus?.subscriptionPlan,
    trialStartDate: trialStatus?.trialStartDate,
    trialEndDate: trialStatus?.trialEndDate,
    nextBillingDate: nextBillingDate
  };

  if (loading) {
    return (
      <PlatformLayout>
        <div className="account-plan-container">
          <div className="account-plan-loading">
            <PlatformText variant="body" translationKey="loading.accountInfo" namespace="account" />
          </div>
        </div>
      </PlatformLayout>
    );
  }

  if (error) {
    return (
      <PlatformLayout>
        <div className="account-plan-container">
          <div className="account-plan-error">
            <PlatformText variant="body" className="error-text">
              <PlatformText translationKey="errors.loadingAccountInfo" namespace="account" />: {error}
            </PlatformText>
            <PlatformButton onClick={handleBack} variant="secondary">
              <PlatformText translationKey="actions.goBack" namespace="account" />
            </PlatformButton>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div className="saas-account-container">
        {/* Modern Header */}
        <div className="saas-header">
          <button 
            className="saas-back-button"
            onClick={handleBack}
            aria-label="Go back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div className="saas-header-content">
            <PlatformText variant="h1" className="saas-title" translationKey="title" namespace="account" />
            <PlatformText variant="body" className="saas-subtitle" translationKey="subtitle" namespace="account" />
          </div>
        </div>


        <div className="saas-layout">
          {/* Account Information Card */}
          <div className="saas-card account-card">
            <div className="saas-card-header">
              <div className="saas-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <PlatformText variant="h3" className="saas-card-title" translationKey="accountInfo.title" namespace="account" />
            </div>
            
            <div className="saas-info-grid">
              <div className="saas-info-item">
                <div className="saas-info-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                  </svg>
                </div>
                <div className="saas-info-content">
                  <PlatformText variant="caption" className="saas-info-label" translationKey="accountInfo.organization" namespace="account" />
                  <PlatformText variant="body" className="saas-info-value">
                    {organizationData?.name || 'N/A'}
                  </PlatformText>
                </div>
              </div>
              
              <div className="saas-info-item">
                <div className="saas-info-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                </div>
                <div className="saas-info-content">
                  <PlatformText variant="caption" className="saas-info-label" translationKey="accountInfo.role" namespace="account" />
                  <PlatformText variant="body" className="saas-info-value">
                    {userRole || 'N/A'}
                  </PlatformText>
                </div>
              </div>
              
              <div className="saas-info-item">
                <div className="saas-info-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <div className="saas-info-content">
                  <PlatformText variant="caption" className="saas-info-label" translationKey="accountInfo.status" namespace="account" />
                  {willNotRenew ? (
                    <div className="saas-status-badge cancelled">
                      <PlatformText variant="body" className="saas-status-text">
                        {(() => {
                          const formattedDate = formatSubscriptionEndDate(subscriptionEndsAt);
                          return formattedDate
                            ? t('subscription.cancelledActive', { date: formattedDate })
                            : t('subscription.cancelledActiveNoDate');
                        })()}
                      </PlatformText>
                    </div>
                  ) : (
                    <div className="saas-status-badge active">
                      <PlatformText variant="body" className="saas-status-text" translationKey="accountInfo.active" namespace="account" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Trial/Subscription Status Card */}
          <div className="saas-card subscription-card">
            <div className="saas-card-header">
              <div className="saas-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <PlatformText variant="h3" className="saas-card-title">
                {displayTrialStatus?.hasActiveSubscription ? t('subscription.title') : t('subscription.trial')}
              </PlatformText>
            </div>

            {displayTrialStatus?.hasActiveSubscription ? (
              /* Enhanced Subscription Display */
              <div className="saas-subscription-card">
                {/* Header: Plan Name + Badge + Status */}
                <div className="saas-subscription-header">
                  <div className="saas-plan-title-section">
                    <div className="saas-plan-name-row">
                      <PlatformText variant="h3" className="saas-plan-name">
                        {getPlanDisplayName(displayTrialStatus?.subscriptionPlan)}
                      </PlatformText>
                    </div>
                    <div className={`saas-renewal-status ${willNotRenew ? 'cancelled' : ''}`}>
                      {willNotRenew ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22,4 12,14.01 9,11.01"/>
                        </svg>
                      )}
                      <PlatformText variant="body" className="saas-renewal-text">
                        {willNotRenew ? (
                          `${t('subscription.endsOn')} ${subscriptionEndsAt ? formatTrialDate(subscriptionEndsAt) : t('subscription.notScheduled')}`
                        ) : (
                          `${t('subscription.renewsOn')} ${displayTrialStatus?.nextBillingDate ? formatTrialDate(displayTrialStatus.nextBillingDate) : t('subscription.notScheduled')}`
                        )}
                      </PlatformText>
                    </div>
                    <div className="saas-plan-price">
                      <PlatformText variant="h4" className="saas-price-amount">
                        {getPlanPrice(displayTrialStatus?.subscriptionPlan)}
                      </PlatformText>
                      <PlatformText variant="caption" className="saas-price-period">
                        {t('subscription.perMonth')}
                      </PlatformText>
                    </div>
                  </div>
                </div>

                {/* Cancellation Banner - Only show when subscription is cancelled */}
                {willNotRenew && (
                  <div className="saas-cancellation-banner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="saas-cancellation-icon">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <div className="saas-cancellation-content">
                      <PlatformText variant="body" className="saas-cancellation-text">
                        {(() => {
                          const formattedDate = formatSubscriptionEndDate(subscriptionEndsAt);
                          return formattedDate
                            ? t('subscription.cancellationBanner', { date: formattedDate })
                            : t('subscription.cancellationBannerNoDate');
                        })()}
                      </PlatformText>
                    </div>
                  </div>
                )}

                {/* Body: Plan Benefits */}
                <div className="saas-plan-benefits">
                  <PlatformText variant="caption" className="saas-benefits-title">
                    {t('subscription.planBenefits')}
                  </PlatformText>
                  <div className="saas-benefits-grid">
                    {getPlanBenefitsWithDetails(displayTrialStatus?.subscriptionPlan, tSubscription).map((benefit, index) => (
                      <div key={index} className="saas-benefit-card">
                        <div className="saas-benefit-icon-container">
                          {benefit.icon}
                        </div>
                        <div className="saas-benefit-content">
                          <PlatformText variant="body" className="saas-benefit-value">
                            {benefit.value}
                          </PlatformText>
                          <PlatformText variant="caption" className="saas-benefit-description">
                            {benefit.description}
                          </PlatformText>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>


                {/* Footer: Actions */}
                <div className="saas-subscription-footer">
                  <div className="saas-subscription-actions">
                    <PlatformButton 
                      variant="secondary" 
                      onClick={handleManageSubscription}
                      className="saas-manage-button"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
                      </svg>
                      {t('subscription.managePlan')}
                    </PlatformButton>
                  </div>
                </div>
              </div>
            ) : (
              /* Trial Status Display */
              <div className="saas-trial-info">
                {displayTrialStatus?.isOnTrial && (
                  <>
                    <div className="saas-trial-status">
                      <div className="saas-trial-badge">
                        <div 
                          className="saas-trial-indicator"
                          style={{ backgroundColor: getTrialStatusColor(displayTrialStatus?.trialStatus, displayTrialStatus?.daysRemaining) }}
                        />
                        <PlatformText variant="body" className="saas-trial-status-text">
                          {getTrialStatusText(displayTrialStatus?.trialStatus, displayTrialStatus?.daysRemaining, t)}
                        </PlatformText>
                      </div>
                      
                      <div className="saas-trial-progress">
                        <div className="saas-progress-header">
                          <PlatformText variant="caption" className="saas-progress-label">
                            {t('subscription.trialProgress')}
                          </PlatformText>
                          <PlatformText variant="caption" className="saas-progress-percentage">
                            {Math.round(displayTrialStatus?.trialProgress)}%
                          </PlatformText>
                        </div>
                        <div className="saas-progress-bar">
                          <div 
                            className="saas-progress-fill"
                            style={{ 
                              width: `${displayTrialStatus?.trialProgress}%`,
                              backgroundColor: getTrialStatusColor(displayTrialStatus?.trialStatus, displayTrialStatus?.daysRemaining)
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="saas-trial-details">
                      <div className="saas-info-item">
                        <div className="saas-info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                        </div>
                        <div className="saas-info-content">
                          <PlatformText variant="caption" className="saas-info-label">
                            {t('subscription.started')}
                          </PlatformText>
                          <PlatformText variant="body" className="saas-info-value">
                            {formatTrialDate(displayTrialStatus?.trialStartDate)}
                          </PlatformText>
                        </div>
                      </div>
                      
                      <div className="saas-info-item">
                        <div className="saas-info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                        </div>
                        <div className="saas-info-content">
                          <PlatformText variant="caption" className="saas-info-label">
                            {t('subscription.expires')}
                          </PlatformText>
                          <PlatformText variant="body" className="saas-info-value">
                            {formatTrialDate(displayTrialStatus?.trialEndDate)}
                          </PlatformText>
                        </div>
                      </div>
                    </div>

                    {displayTrialStatus?.isTrialExpired && (
                      <div className="saas-warning expired">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <PlatformText variant="body" className="saas-warning-text">
                          {t('subscription.trialExpired')}
                        </PlatformText>
                      </div>
                    )}

                    {daysRemaining <= 3 && daysRemaining > 0 && (
                      <div className="saas-warning urgent">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                          <line x1="12" y1="9" x2="12" y2="13"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <PlatformText variant="body" className="saas-warning-text">
                          {t('subscription.trialExpiring', { days: daysRemaining || 0 })}
                        </PlatformText>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {/* Manage Plan Modal */}
            <Modal isOpen={manageOpen} onClose={closeManageDialog} title={t('modals.manageSubscription')}>
              <div className="modal-form">
                <div className="form-group">
                  <label>{t('planManagement.currentPlan')}</label>
                  <input className="disabled-input" value={getPlanDisplayName(displayTrialStatus?.subscriptionPlan)} disabled />
                </div>
                <div className="form-group">
                  <label>{willNotRenew ? t('subscription.accessEndsDate') : t('planManagement.renewalDate')}</label>
                  <input 
                    className={`disabled-input ${willNotRenew ? 'cancelled' : ''}`} 
                    value={willNotRenew 
                      ? (subscriptionEndsAt ? formatTrialDate(subscriptionEndsAt) : t('subscription.notScheduled'))
                      : (displayTrialStatus?.nextBillingDate ? formatTrialDate(displayTrialStatus.nextBillingDate) : t('subscription.notScheduled'))
                    } 
                    disabled 
                  />
                  {willNotRenew && (
                    <PlatformText variant="caption" className="modal-cancellation-notice">
                      {t('subscription.cancellationScheduled')}
                    </PlatformText>
                  )}
                </div>
                <div className="modal-actions">
                  {/* Cancel Subscription Button - Show on ALL platforms */}
                  <button className="cancel-button" onClick={handleCancelSubscription}>
                    {t('planManagement.cancelAutoRenew')}
                  </button>
                  
                  {/* Upgrade & Billing Buttons - Web ONLY */}
                  {!isNative && (
                    <button className="submit-button" onClick={handleUpgradePlanFromDialog}>
                      {t('planManagement.upgradePlan')}
                    </button>
                  )}
                  
                  {/* Native Info Message */}
                  {isNative && (
                    <div className="native-modal-info">
                      <PlatformText variant="body" className="native-info-text">
                        Plan upgrades and billing settings are handled in the primary web portal.
                      </PlatformText>
                    </div>
                  )}
                </div>
              </div>
            </Modal>
          </div>

          {/* Usage Statistics Card */}
          <div className="saas-card usage-card">
            <div className="saas-card-header">
              <div className="saas-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
                </svg>
              </div>
              <PlatformText variant="h3" className="saas-card-title">
                {t('usage.title')}
              </PlatformText>
            </div>
            
            {usageLoading ? (
              <div className="saas-usage-loading">
                <PlatformText variant="body">{t('usage.loading')}</PlatformText>
              </div>
            ) : usageError ? (
              <div className="saas-usage-error">
                <PlatformText variant="body" className="error-text">
                  {t('usage.error', { error: usageError })}
                </PlatformText>
              </div>
            ) : usageStats ? (
              <div className="saas-usage-stats">
                <div className="saas-usage-item">
                  <div className="saas-usage-header">
                    <PlatformText variant="body" className="saas-usage-label">
                      {t('usage.orders')}
                    </PlatformText>
                    <PlatformText variant="body" className="saas-usage-value">
                      {usageStats.orders.bypassed ? t('usage.bypassed') : 
                       usageStats.orders.limit === -1 ? t('usage.unlimited') : 
                       `${usageStats.orders.current} / ${usageStats.orders.limit}`}
                    </PlatformText>
                  </div>
                  {!usageStats.orders.bypassed && usageStats.orders.limit !== -1 && (
                    <div className="saas-usage-progress">
                      <div 
                        className="saas-usage-progress-bar"
                        style={{ 
                          width: `${Math.min(100, (usageStats.orders.current / usageStats.orders.limit) * 100)}%` 
                        }}
                      />
                    </div>
                  )}
                  <PlatformText variant="caption" className="saas-usage-remaining">
                    {usageStats.orders.bypassed 
                      ? t('usage.bypassedUnlimited')
                      : usageStats.orders.remaining > 0 
                        ? t('usage.remaining', { count: usageStats.orders.remaining })
                        : usageStats.orders.limit === -1 
                          ? t('usage.unlimited')
                          : t('usage.limitReached')
                    }
                  </PlatformText>
                </div>
                
                <div className="saas-usage-item">
                  <div className="saas-usage-header">
                    <PlatformText variant="body" className="saas-usage-label">
                      {t('usage.users')}
                    </PlatformText>
                    <PlatformText variant="body" className="saas-usage-value">
                      {usageStats.users.bypassed ? t('usage.bypassed') : 
                       usageStats.users.limit === -1 ? t('usage.unlimited') : 
                       `${usageStats.users.current} / ${usageStats.users.limit}`}
                    </PlatformText>
                  </div>
                  {!usageStats.users.bypassed && usageStats.users.limit !== -1 && (
                    <div className="saas-usage-progress">
                      <div 
                        className="saas-usage-progress-bar"
                        style={{ 
                          width: `${Math.min(100, (usageStats.users.current / usageStats.users.limit) * 100)}%` 
                        }}
                      />
                    </div>
                  )}
                  <PlatformText variant="caption" className="saas-usage-remaining">
                    {usageStats.users.bypassed 
                      ? t('usage.bypassedUnlimited')
                      : usageStats.users.remaining > 0 
                        ? t('usage.remaining', { count: usageStats.users.remaining })
                        : usageStats.users.limit === -1 
                          ? t('usage.unlimited')
                          : t('usage.limitReached')
                    }
                  </PlatformText>
                </div>
              </div>
            ) : null}
          </div>

          {/* Plan Management Card */}
          <div className="saas-card plan-management-card">
            <div className="saas-card-header">
              <div className="saas-card-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <PlatformText variant="h3" className="saas-card-title">
                {t('planManagement.title')}
              </PlatformText>
            </div>
            
            <div className="saas-plan-actions">
              {!hasActiveSubscription && (
                <PlatformButton 
                  onClick={handleUpgrade}
                  variant="primary"
                  className="saas-plan-upgrade-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  {isTrialExpired ? t('subscription.upgradeNow') : t('subscription.upgradePlan')}
                </PlatformButton>
              )}
              
              <PlatformButton 
                onClick={handleContactSupport}
                variant="secondary"
                className="saas-plan-support-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {t('subscription.contactSupport')}
              </PlatformButton>
            </div>
          </div>

          {/* Subscription Plans Preview - Conditional rendering based on platform */}
          {(!hasActiveSubscription || willNotRenew) && !plansLoading && (
            <div className="saas-card plans-preview-card">
              {isNative ? (
                // Native: Show plans without pricing and buttons
                <NativePlanDisplay 
                  plans={plans}
                  t={t}
                />
              ) : (
                // Web: Show full plans with pricing and buttons
                <>
              <div className="saas-card-header">
                <div className="saas-card-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <PlatformText variant="h3" className="saas-card-title">
                  {t('subscription.availablePlans')}
                </PlatformText>
              </div>

              {/* Billing Toggle */}
              <div className="saas-billing-toggle">
                <button 
                  className={clsx('saas-toggle-option', billingInterval === 'month' && 'active')}
                  onClick={() => setBillingInterval('month')}
                >
                  {t('subscription.monthly')}
                </button>
                <button 
                  className={clsx('saas-toggle-option', billingInterval === 'year' && 'active')}
                  onClick={() => setBillingInterval('year')}
                >
                  {t('subscription.yearly')}
                  <span className="saas-savings-badge">{t('subscription.saveUpTo')}</span>
                </button>
              </div>

              {/* Horizontal Scrolling Plans Container */}
              <div className="saas-plans-container">
                <div 
                  className="saas-plans-scroll-wrapper"
                  role="region"
                  aria-label="Subscription plans carousel"
                  tabIndex="0"
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                      e.preventDefault();
                      const container = e.target;
                      const scrollAmount = 300; // Adjust based on card width
                      if (e.key === 'ArrowLeft') {
                        container.scrollLeft -= scrollAmount;
                      } else {
                        container.scrollLeft += scrollAmount;
                      }
                    }
                  }}
                >
                  <div className="saas-plans-grid">
                    {plans.map(plan => {
                      const price = billingInterval === 'year' ? plan.yearlyPrice : plan.monthlyPrice;
                      const isPopular = plan.popular;
                      const isCurrentPlan = hasActiveSubscription && trialStatus?.subscriptionPlan === plan.id;
                      
                        return (
                          <div 
                            key={plan.id}
                            className={`saas-plan-item ${isPopular ? 'popular' : ''}`}
                            role="article"
                            aria-label={`${plan.name} subscription plan`}
                          >
                          {isPopular && (
                            <div className="saas-plan-badge">{t('subscription.mostPopular')}</div>
                          )}
                          
                          <div className="saas-plan-header">
                            <PlatformText variant="h4" className="saas-plan-name">
                              {plan.name}
                            </PlatformText>
                            <PlatformText variant="body" className="saas-plan-description">
                              {plan.description}
                            </PlatformText>
                          </div>

                          <div className="saas-plan-pricing">
                            <PlatformText variant="h3" className="saas-plan-price">
                              {formatPrice(price)}
                            </PlatformText>
                            <PlatformText variant="caption" className="saas-plan-period">
                              {billingInterval === 'year' ? t('subscription.perYear') : t('subscription.perMonth')}
                            </PlatformText>
                          </div>

                          <div className="saas-plan-limits">
                            <div className="saas-limit-item">
                              <PlatformText variant="body" className="saas-limit-value">
                                {plan.limits.maxUsers === -1 ? '∞' : plan.limits.maxUsers}
                              </PlatformText>
                              <PlatformText variant="caption" className="saas-limit-label">
                                {t('subscription.users')}
                              </PlatformText>
                            </div>
                            <div className="saas-limit-item">
                              <PlatformText variant="body" className="saas-limit-value">
                                {plan.limits.maxOrders === -1 ? '∞' : plan.limits.maxOrders}
                              </PlatformText>
                              <PlatformText variant="caption" className="saas-limit-label">
                                {t('subscription.orders')}
                              </PlatformText>
                            </div>
                            <div className="saas-limit-item">
                              <PlatformText variant="body" className="saas-limit-value">
                                {plan.limits.backupDays === -1 ? '∞' : `${plan.limits.backupDays}d`}
                              </PlatformText>
                              <PlatformText variant="caption" className="saas-limit-label">
                                {t('subscription.backup')}
                              </PlatformText>
                            </div>
                          </div>

                          <PlatformButton 
                            onClick={() => handlePlanSelect(plan)}
                            variant={isPopular ? 'primary' : 'secondary'}
                            className={clsx('saas-plan-button', isCurrentPlan && 'current-plan')}
                            disabled={isCurrentPlan}
                          >
                            {isCurrentPlan
                              ? t('subscription.currentPlan')
                              : t('subscription.choosePlan', { planName: plan.name })}
                          </PlatformButton>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>

              </>
            )}
            </div>
          )}
        </div>

        {/* Sticky Upgrade Button for Mobile - Web Only */}
        {!isNative && !hasActiveSubscription && (
          <div className="saas-mobile-upgrade">
            <PlatformButton 
              onClick={handleUpgrade}
              variant="primary"
              className="saas-mobile-upgrade-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              {isTrialExpired ? t('subscription.upgradeNow') : t('subscription.upgradePlan')}
            </PlatformButton>
          </div>
        )}

        {/* Upgrade Modal - Web Only */}
        {!isNative && (
          <SubscriptionUpgradeModal
            isOpen={showUpgradeModal}
            onClose={handleUpgradeModalClose}
            selectedPlan={selectedUpgradePlan}
            onUpgradeComplete={handleUpgradeComplete}
            showCurrentPlan={true}
          />
        )}
      </div>
    </PlatformLayout>
  );
};

export default AccountPlan;
